// BaseAdminService.js

// Firebase
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { collections, db, functions, httpsCallable } from 'firebaseConfig';
import { buildTenantCallableFields } from 'utilities/basePersistence';

const LIVE_ROOMS_COLLECTION = 'live_rooms';
const LIVE_USAGE_MONTHLY_COLLECTION = 'live_usage_monthly';
const callable = (name) => httpsCallable(functions, name);

/**
 * toCurrentUtcMonthKey
 *
 * Builds a UTC month key in YYYY-MM format.
 *
 * @returns {string} Current UTC month key.
 */
function toCurrentUtcMonthKey() {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * normalizeNonNegativeInteger
 *
 * Parses a non-negative integer with fallback.
 *
 * @param {unknown} value - Raw numeric value.
 * @param {number} fallback - Fallback integer.
 *
 * @returns {number} Parsed non-negative integer.
 */
function normalizeNonNegativeInteger(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const whole = Math.floor(parsed);
  if (whole < 0) return fallback;
  return whole;
}

/**
 * normalizeBaseRow
 *
 * Normalizes an admin base row from Firestore.
 *
 * @param {object} docSnap - Firestore document snapshot.
 *
 * @returns {object} Normalized base row.
 */
function normalizeBaseRow(docSnap) {
  const row = docSnap?.data() || {};
  const key = String(row.key || docSnap.id || '').trim() || docSnap.id;
  const rawMemberCount = Number(row.memberCount);
  const memberCount = Number.isFinite(rawMemberCount) && rawMemberCount >= 0
    ? Math.floor(rawMemberCount)
    : 0;

  return {
    ...row,
    id: docSnap.id,
    key,
    memberCount,
    ownerKey: String(row.ownerKey || row.userKey || '').trim(),
    planKey: String(row.planKey || '').trim(),
    planTitle: String(row.planTitle || '').trim(),
    title: String(row.title || 'Untitled Base').trim() || 'Untitled Base',
  };
}

/**
 * Lists all bases for the admin console.
 *
 * @returns {Promise<Array<object>>} Base rows sorted by title then key.
 */
export async function listAdminBases() {
  const snapshot = await getDocs(collection(db, collections.bases));

  return snapshot.docs
    .map(normalizeBaseRow)
    .sort((left, right) => {
      const titleSort = left.title.localeCompare(right.title);
      if (titleSort !== 0) return titleSort;
      return left.key.localeCompare(right.key);
    });
}

/**
 * Resolves base titles for a set of base keys (admin reads).
 *
 * @param {string[]} baseKeys - Base keys to resolve.
 * @returns {Promise<Record<string, string>>} Map of baseKey → title (empty if missing).
 */
export async function getBaseTitlesByKeys(baseKeys = []) {
  const uniqueKeys = [...new Set(
    (Array.isArray(baseKeys) ? baseKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean),
  )];

  const pairs = await Promise.all(uniqueKeys.map(async (key) => {
    try {
      const snap = await getDoc(doc(db, collections.bases, key));
      if (!snap.exists()) return [key, ''];
      const title = String(snap.data()?.title || '').trim();
      return [key, title];
    } catch {
      return [key, ''];
    }
  }));

  return Object.fromEntries(pairs);
}

/**
 * Updates a base plan assignment.
 *
 * @param {object} input - Update input.
 * @param {string} input.baseKey - Base key.
 * @param {string} input.planKey - Plan key or empty string to clear.
 * @param {string} input.planTitle - Plan title or empty string to clear.
 * @param {string} input.assignedBy - User key of the admin making assignment.
 *
 * @returns {Promise<void>}
 */
export async function updateBasePlanAssignment({
  baseKey,
  planKey,
}) {
  const cleanedBaseKey = String(baseKey || '').trim();

  if (!cleanedBaseKey) {
    throw new Error('baseKey is required');
  }

  const cleanedPlanKey = String(planKey || '').trim();

  const fn = callable('adminAssignBasePlan');
  const { data } = await fn({
    ...buildTenantCallableFields(cleanedBaseKey),
    planKey: cleanedPlanKey,
  });

  return data || null;
}

/**
 * Deletes a base via the platform-admin callable (hardened delete pipeline).
 *
 * @param {object} input
 * @param {string} input.baseKey - Base key to delete.
 *
 * @returns {Promise<object>} Delete operation result.
 */
export async function deleteAdminBase({ baseKey }) {
  const cleanedBaseKey = String(baseKey || '').trim();
  if (!cleanedBaseKey) {
    throw new Error('baseKey is required');
  }

  // Match server timeoutSeconds: 540 for large base trees.
  const fn = httpsCallable(functions, 'adminDeleteBase', { timeout: 540000 });
  const { data } = await fn({
    ...buildTenantCallableFields(cleanedBaseKey),
  });

  return data || null;
}

/**
 * Reads current-month live usage and active room stats for a base.
 *
 * @param {object} input - Read input.
 * @param {string} input.baseKey - Base key.
 * @param {string} [input.monthKey] - Optional month key override (YYYY-MM).
 *
 * @returns {Promise<object>} Live usage snapshot.
 */
export async function getBaseLiveUsageSnapshot({
  baseKey,
  monthKey,
}) {
  const cleanedBaseKey = String(baseKey || '').trim();
  if (!cleanedBaseKey) {
    throw new Error('baseKey is required');
  }

  const resolvedMonthKey = String(monthKey || '').trim() || toCurrentUtcMonthKey();
  const baseRef = doc(db, collections.bases, cleanedBaseKey);
  const usageRef = doc(baseRef, LIVE_USAGE_MONTHLY_COLLECTION, resolvedMonthKey);

  const activeRoomsQuery = query(
    collection(baseRef, LIVE_ROOMS_COLLECTION),
    where('isActive', '==', true)
  );

  const [usageSnap, activeRoomsSnap] = await Promise.all([
    getDoc(usageRef),
    getDocs(activeRoomsQuery),
  ]);

  const usageRow = usageSnap.exists() ? (usageSnap.data() || {}) : {};
  const activeRooms = activeRoomsSnap.docs.map((roomSnap) => roomSnap.data() || {});

  const activeRoomCount = activeRooms.length;
  const activeParticipantCount = activeRooms.reduce((total, roomRow) => {
    return total + normalizeNonNegativeInteger(roomRow?.participantCount, 0);
  }, 0);

  return {
    activeParticipantCount,
    activeRoomCount,
    dateModified: usageRow?.dateModified || null,
    egressSeconds: normalizeNonNegativeInteger(usageRow?.egressSeconds, 0),
    monthKey: resolvedMonthKey,
    participantSeconds: normalizeNonNegativeInteger(usageRow?.participantSeconds, 0),
  };
}
